import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './components/Auth/Login';
import Register from './components/Auth/Register';
import VerifyEmail from './components/Auth/VerifyEmail';
import ForgotPassword from './components/Auth/ForgotPassword';
import ResetPassword from './components/Auth/ResetPassword';
import OutlookCallback from './components/Auth/OutlookCallback';
import GoogleCallback from './components/Auth/GoogleCallback';
import Dashboard from './components/Dashboard/Dashboard';
import MeetingDetail from './components/Meetings/MeetingDetail';
import SharedMeetingView from './components/Meetings/SharedMeetingView';
import UploadMeeting from './components/Meetings/UploadMeeting';
import Scheduler from './components/Scheduler/Scheduler';
import Settings from './components/Settings/Settings';
import EmailDebug from './components/Email/EmailDebug';
import LiveRecording from './components/Recordings/LiveRecording';
import Layout from './components/Layout/Layout';
import MainPage from './pages/MainPage';
import PricingPage from './pages/PricingPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import ContactPage from './pages/ContactPage';
import NotFoundPage from './pages/NotFoundPage';
import HostDashboard from './components/HostedMeetings/HostDashboard';
import MeetingRoomEnhanced from './components/HostedMeetings/MeetingRoomEnhanced';
import AdminConsole from './components/Admin/AdminConsole';
import AnalyticsDashboard from './components/Analytics/AnalyticsDashboard';
import MeetingAnalyticsSummary from './components/Analytics/MeetingAnalyticsSummary';
import AlertSettingsForm from './components/Analytics/AlertSettingsForm';
import RateLimitNotification from './components/common/RateLimitNotification';
import Upgrade from './components/Payment/Upgrade';
import CreateOrganization from './components/Organization/CreateOrganization';
import JoinOrganization from './components/Organization/JoinOrganization';
import OrganizationManagement from './components/Organization/OrganizationManagement';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
});

function PrivateRoute({ children }) {
  const { user } = useAuth();
  return user ? children : <Navigate to="/login" />;
}

function PublicRoute({ children }) {
  const { user } = useAuth();
  // Only redirect to dashboard if user is logged in and trying to access /login
  return !user ? children : <Navigate to="/dashboard" />;
}

function AdminRoute({ children }) {
  const { user } = useAuth();
  if (!user) {
    return <Navigate to="/login" />;
  }
  if (user.role !== 'admin') {
    return <Navigate to="/dashboard" />;
  }
  return children;
}

function OrgAdminRoute({ children }) {
  const { user } = useAuth();
  if (!user) {
    return <Navigate to="/login" />;
  }
  if (!user.organization || user.orgRole !== 'org_admin') {
    return <Navigate to="/dashboard" />;
  }
  return children;
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <RateLimitNotification />
          <Routes>
            {/* Public marketing pages */}
            <Route path="/" element={<MainPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/contact" element={<ContactPage />} />
            
            {/* Auth routes */}
            <Route 
              path="/login" 
              element={
                <PublicRoute>
                  <Login />
                </PublicRoute>
              } 
            />
            <Route 
              path="/register" 
              element={
                <PublicRoute>
                  <Register />
                </PublicRoute>
              } 
            />
            <Route 
              path="/verify-email" 
              element={<VerifyEmail />}
            />
            <Route 
              path="/forgot-password" 
              element={
                <PublicRoute>
                  <ForgotPassword />
                </PublicRoute>
              } 
            />
            <Route 
              path="/reset-password" 
              element={<ResetPassword />}
            />
            <Route 
              path="/outlook/callback" 
              element={<OutlookCallback />}
            />
            <Route 
              path="/google/callback" 
              element={<GoogleCallback />}
            />
            
            {/* Shared meeting view (public) */}
            <Route 
              path="/shared/:token" 
              element={<SharedMeetingView />}
            />
            
            {/* Private app routes */}
            <Route 
              path="/dashboard" 
              element={
                <PrivateRoute>
                  <Layout>
                    <Dashboard />
                  </Layout>
                </PrivateRoute>
              } 
            />
            <Route 
              path="/upload" 
              element={
                <PrivateRoute>
                  <Layout>
                    <UploadMeeting />
                  </Layout>
                </PrivateRoute>
              } 
            />
            <Route 
              path="/record" 
              element={
                <PrivateRoute>
                  <Layout>
                    <LiveRecording />
                  </Layout>
                </PrivateRoute>
              } 
            />
            <Route 
              path="/scheduler" 
              element={
                <PrivateRoute>
                  <Layout>
                    <Scheduler />
                  </Layout>
                </PrivateRoute>
              } 
            />
            <Route 
              path="/email-debug" 
              element={
                <PrivateRoute>
                  <Layout>
                    <EmailDebug />
                  </Layout>
                </PrivateRoute>
              } 
            />
            <Route 
              path="/meeting/:id" 
              element={
                <PrivateRoute>
                  <Layout>
                    <MeetingDetail />
                  </Layout>
                </PrivateRoute>
              } 
            />
            <Route 
              path="/settings" 
              element={
                <PrivateRoute>
                  <Layout>
                    <Settings />
                  </Layout>
                </PrivateRoute>
              } 
            />
            <Route 
              path="/host-dashboard" 
              element={
                <PrivateRoute>
                  <Layout>
                    <HostDashboard />
                  </Layout>
                </PrivateRoute>
              } 
            />
            <Route 
              path="/meeting-room/:meetingId" 
              element={
                <PrivateRoute>
                  <MeetingRoomEnhanced />
                </PrivateRoute>
              } 
            />
            <Route 
              path="/join-meeting/:meetingCode" 
              element={
                <PrivateRoute>
                  <MeetingRoomEnhanced />
                </PrivateRoute>
              } 
            />
            <Route 
              path="/analytics" 
              element={
                <PrivateRoute>
                  <Layout>
                    <AnalyticsDashboard />
                  </Layout>
                </PrivateRoute>
              } 
            />
            <Route 
              path="/analytics/:id" 
              element={
                <PrivateRoute>
                  <Layout>
                    <MeetingAnalyticsSummary />
                  </Layout>
                </PrivateRoute>
              } 
            />
            <Route 
              path="/analytics/settings" 
              element={
                <PrivateRoute>
                  <Layout>
                    <AlertSettingsForm />
                  </Layout>
                </PrivateRoute>
              } 
            />
            
            {/* Admin route */}
            <Route 
              path="/admin" 
              element={
                <AdminRoute>
                  <Layout>
                    <AdminConsole />
                  </Layout>
                </AdminRoute>
              } 
            />
            
            {/* Upgrade route */}
            <Route 
              path="/upgrade" 
              element={
                <PrivateRoute>
                  <Layout>
                    <Upgrade />
                  </Layout>
                </PrivateRoute>
              } 
            />
            
            {/* Organization routes */}
            <Route 
              path="/create-org" 
              element={
                <PrivateRoute>
                  <Layout>
                    <CreateOrganization />
                  </Layout>
                </PrivateRoute>
              } 
            />
            <Route 
              path="/join-org" 
              element={
                <PrivateRoute>
                  <Layout>
                    <JoinOrganization />
                  </Layout>
                </PrivateRoute>
              } 
            />
            <Route 
              path="/organization" 
              element={
                <OrgAdminRoute>
                  <Layout>
                    <OrganizationManagement />
                  </Layout>
                </OrgAdminRoute>
              } 
            />
            
            {/* 404 page */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
